import { Link } from "react-router-dom";
import { Sparkles, BarChart3, Cloud, Brain, ClipboardList, ArrowRight, QrCode, Palette, Building2, GraduationCap, Film } from "lucide-react";
import { Logo } from "../components/Logo";
import { QuizSurveyShowcase } from "../components/landing/QuizSurveyShowcase";
import { VybeInActionGallery } from "../components/landing/VybeInActionGallery";
import { RemoteControlShowcase } from "../components/landing/RemoteControlShowcase";
import { FeatureCatalog } from "../components/landing/FeatureCatalog";
import { InfographicShowcase } from "../components/landing/InfographicShowcase";
import { OpenTextShowcase } from "../components/landing/OpenTextShowcase";
import { BrainstormSection } from "../components/landing/BrainstormSection";
import { ParticipantShowcase } from "../components/landing/ParticipantShowcase";
import { LanguageShowcase } from "../components/landing/LanguageShowcase";
import { TranslationCelebrationShowcase } from "../components/landing/TranslationCelebrationShowcase";
import { DemoVideoSection } from "../components/landing/DemoVideoSection";

const FEATURES = [
  {
    icon: Brain,
    title: "AI Quiz Generator",
    desc: "Type a topic, pick a difficulty, and get a full quiz with correct answers in seconds.",
    span: "lg:col-span-2 lg:row-span-2",
    accent: true,
    screenshot: "/screenshots/ai_quiz_generator.png",
  },
  {
    icon: BarChart3,
    title: "Live Polling",
    desc: "Bar charts update the instant a vote comes in — no refresh needed.",
    span: "",
  },
  {
    icon: Cloud,
    title: "Word Cloud",
    desc: "Free-text answers grow into a living cloud, biggest words rise to the top.",
    span: "",
  },
  {
    icon: ClipboardList,
    title: "Quiz",
    desc: "Timed multiple choice with a live leaderboard and reveal-the-answer suspense.",
    span: "",
  },
  {
    icon: Sparkles,
    title: "Survey",
    desc: "Star-rating surveys with instant averages and response histograms.",
    span: "",
  },
  {
    icon: Film,
    title: "Media Slide",
    desc: "Play a video, photo, or YouTube/video link first \u2014 then reveal any poll, quiz or survey question right after.",
    span: "",
  },
  {
    icon: Palette,
    title: "Your Brand, Everywhere",
    desc: "Primary & accent colors, logo, and fonts apply across dashboard and audience screens.",
    span: "lg:col-span-2",
  },
  {
    icon: Building2,
    title: "Built for Teams & Orgs",
    desc: "Multi-tenant from day one — every organization gets its own workspace and look.",
    span: "",
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#FAFAFC] text-[#0F172A] font-body overflow-x-hidden">
      <header className="fixed top-0 inset-x-0 z-50">
        <Link
          to="/join"
          data-testid="mobile-participant-banner"
          className="lg:hidden pulse-ring flex items-center justify-center gap-1.5 text-white text-[11px] sm:text-sm font-semibold py-2.5 px-3 text-center whitespace-nowrap transition-colors hover:brightness-110"
          style={{ background: "linear-gradient(90deg, #4F46E5, #FF5733)" }}
        >
          <QrCode className="w-3.5 h-3.5 shrink-0" />
          Scan QR or enter code to join
          <ArrowRight className="w-3 h-3 shrink-0" />
        </Link>
        <nav className="glass-nav bg-white/80 border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo className="h-11" />
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3">
            <Link
              to="/join"
              data-testid="nav-join-link"
              className="hidden lg:flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#4F46E5] transition-colors"
            >
              <QrCode className="w-4 h-4" /> <span>I have a code</span>
            </Link>
            <Link
              to="/login"
              data-testid="nav-login-button"
              className="hidden sm:inline text-xs sm:text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors px-2 sm:px-3 py-2 whitespace-nowrap"
            >
              Log in
            </Link>
            <Link
              to="/register"
              data-testid="nav-get-started-button"
              className="text-xs sm:text-sm font-semibold text-white px-3 sm:px-4 py-2 rounded-full transition-transform hover:scale-105 active:scale-95 whitespace-nowrap"
              style={{ background: "#4F46E5" }}
            >
              Get started<span className="hidden sm:inline">&nbsp;free</span>
            </Link>
          </div>
        </div>
        </nav>
      </header>

      <section className="relative overflow-hidden pt-48 lg:pt-40 pb-24 px-6 max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <div className="absolute -top-10 -left-24 w-96 h-96 rounded-full bg-[#4F46E5]/10 blur-3xl" />
        <div className="absolute top-40 -right-10 w-72 h-72 rounded-full bg-[#FF5733]/10 blur-3xl" />
        <div className="relative z-10">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#4F46E5] bg-[#4F46E5]/10 px-3 py-1.5 rounded-full mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Live audience engagement
          </span>
          <h1 className="font-heading text-[1.65rem] sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] sm:leading-[1.05] mb-6">
            <span className="block whitespace-nowrap">Turn any room into a</span>
            <span className="block whitespace-nowrap" style={{ color: "#4F46E5" }}>Live conversation</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-lg mb-8">
            AI-generated quizzes, live polls, word clouds and surveys — presented full-screen,
            answered from any phone, updating in real time. Built for teams, styled as your brand.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/join"
              data-testid="hero-join-button"
              className="order-1 lg:order-2 inline-flex items-center gap-2 font-semibold px-6 py-3.5 rounded-full border-2 border-slate-200 hover:border-[#4F46E5] transition-colors"
            >
              Join a session <QrCode className="w-4 h-4" />
            </Link>
            <Link
              to="/register"
              data-testid="hero-get-started-button"
              className="order-2 lg:order-1 inline-flex items-center gap-2 text-white font-semibold px-6 py-3.5 rounded-full transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-indigo-500/30"
              style={{ background: "#4F46E5" }}
            >
              Get started free <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-xs text-slate-400 mt-4">No credit card. Full access to polls, quizzes, word clouds and surveys, plus a fully editable demo.</p>
          <p className="sm:hidden text-sm text-slate-500 mt-5">
            Already have an account?{" "}
            <Link to="/login" data-testid="mobile-hero-login-link" className="font-semibold text-[#4F46E5] hover:underline">
              Log in
            </Link>
          </p>
        </div>

        <div className="relative z-10 float-slow">
          <div className="rounded-3xl overflow-hidden shadow-2xl border-8 border-white rotate-2">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/742a2425-99e6-4da6-8013-d806e8e0850f/images/34aff5306105331e321cc155b68201d0cb836cea043074829136988971acbe72.jpeg"
              alt="A full conference room of engineers laughing and voting live on their phones, with the presenter screen showing live poll results in the background"
              className="w-full h-[420px] object-cover"
              fetchPriority="high"
              decoding="async"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 w-56 -rotate-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Live poll</p>
            <div className="space-y-1.5">
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full w-4/5 rounded-full" style={{ background: "#4F46E5" }} /></div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full w-2/5 rounded-full" style={{ background: "#FF5733" }} /></div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full w-3/5 rounded-full" style={{ background: "#4F46E5" }} /></div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 max-w-7xl mx-auto pb-28">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3">Everything your team needs to run a live session</h2>
        <p className="text-slate-600 max-w-2xl mb-12">Five core tools, one join code.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:auto-rows-[180px]">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`relative rounded-2xl p-6 border overflow-hidden flex flex-col ${f.span} ${
                f.accent ? "bg-[#0F172A] text-white border-transparent" : "bg-white border-slate-200"
              }`}
            >
              <div
                className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${f.accent ? "bg-white/10" : ""}`}
                style={!f.accent ? { background: "#4F46E5" } : {}}
              >
                <f.icon className="w-5.5 h-5.5 text-white" />
              </div>
              <h3 className="font-heading text-lg sm:text-xl font-semibold mb-1.5">{f.title}</h3>
              <p className={`text-sm leading-relaxed ${f.accent ? "text-white/70" : "text-slate-600"}`}>{f.desc}</p>
              {f.screenshot && (
                <div className="mt-4 rounded-xl overflow-hidden border border-white/10 flex-1">
                  <img src={f.screenshot} alt="AI Quiz Generator interface" className="w-full h-full object-cover object-top" data-testid="feature-card-ai-quiz-generator-screenshot" />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <ParticipantShowcase />
      <QuizSurveyShowcase />

      <section className="px-6 pb-24" data-testid="section-learning-guide">
        <div className="max-w-5xl mx-auto rounded-3xl border border-slate-200 bg-white p-10 sm:p-14 grid sm:grid-cols-[auto_1fr_auto] gap-6 items-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "#4F46E5" }}>
            <GraduationCap className="w-7 h-7 text-white" />
          </div>
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#4F46E5] bg-[#4F46E5]/10 px-3 py-1.5 rounded-full mb-3">
              Learning
            </span>
            <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight mb-2">New here? Follow the Step-by-Step Guide</h2>
            <p className="text-slate-600 leading-relaxed max-w-xl">
              From your first login to running a full Bible study session with AI-generated quizzes and illustrations —
              every feature explained simply, in order.
            </p>
          </div>
          <Link
            to="/guide"
            data-testid="homepage-step-by-step-guide-link"
            className="inline-flex items-center justify-center gap-2 text-white font-semibold px-6 py-3.5 rounded-full transition-transform hover:scale-105 active:scale-95 whitespace-nowrap"
            style={{ background: "#FF5733" }}
          >
            Step-by-Step Guide <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <InfographicShowcase />
      <OpenTextShowcase />
      <VybeInActionGallery />
      <DemoVideoSection />
      <RemoteControlShowcase />
      <LanguageShowcase />
      <BrainstormSection />
      <TranslationCelebrationShowcase />
      <FeatureCatalog />

      <section className="px-6 pb-16" data-testid="section-contact-cta">
        <div className="max-w-5xl mx-auto rounded-3xl border border-slate-200 bg-white p-10 sm:p-14 grid sm:grid-cols-2 gap-8 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#4F46E5] bg-[#4F46E5]/10 px-3 py-1.5 rounded-full mb-4">
              Talk to us
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">Got a feature idea, or need a hand getting set up?</h2>
            <p className="text-slate-600 leading-relaxed">
              Whether you want a custom feature, need help onboarding your whole team, or just have a
              question — our team reads every message and gets back to you fast.
            </p>
          </div>
          <div className="flex flex-col sm:items-end gap-3">
            <Link
              to="/contact"
              data-testid="homepage-contact-us-button"
              className="inline-flex items-center justify-center gap-2 text-white font-semibold px-6 py-3.5 rounded-full transition-transform hover:scale-105 active:scale-95 w-full sm:w-auto"
              style={{ background: "#4F46E5" }}
            >
              Contact us <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-xs text-slate-400 sm:text-right">Feature requests &bull; Team onboarding &bull; General support</p>
          </div>
        </div>
      </section>

      <section className="px-6 pb-28">
        <div className="max-w-5xl mx-auto rounded-3xl bg-[#0B0F19] text-white p-12 sm:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 stage-radial opacity-80" />
          <div className="relative z-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">Ready to make your next meeting interactive?</h2>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">Get started free and explore a full demo session — polls, quizzes, word clouds and surveys, live.</p>
            <Link
              to="/register"
              data-testid="cta-get-started-button"
              className="inline-flex items-center gap-2 text-white font-semibold px-7 py-4 rounded-full transition-transform hover:scale-105 active:scale-95"
              style={{ background: "#4F46E5" }}
            >
              Get started free <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="px-6 py-8 border-t border-slate-200 text-center text-sm text-slate-500">
        &copy; 2026 VybeMeter &nbsp;|&nbsp; Designed by Geneza Solutions Private Limited &nbsp;|&nbsp;{" "}
        <Link to="/contact" data-testid="footer-contact-link" className="text-[#4F46E5] font-medium hover:underline">
          Contact us
        </Link>
        &nbsp;|&nbsp;{" "}
        <Link to="/terms" data-testid="footer-terms-link" className="text-[#4F46E5] font-medium hover:underline">
          Terms &amp; Conditions
        </Link>
        &nbsp;|&nbsp;{" "}
        <Link to="/privacy" data-testid="footer-privacy-link" className="text-[#4F46E5] font-medium hover:underline">
          Privacy Policy
        </Link>
      </footer>
    </div>
  );
}
